import { useEffect, useState } from "react";
import { fetchRecentScans, type RecentScan } from "../services/scans";

interface KioskScanFeed {
  scans: RecentScan[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  refresh: () => void;
}

export function useKioskScans(
  pollMs = 5000,
  limit?: number,
  hospitalId?: string,
  period: string = 'today',
  startDate?: string,
  endDate?: string
): KioskScanFeed {
  const [scans, setScans] = useState<RecentScan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Built per-effect run from primitive deps so the object identity is stable.
  const dateRange = startDate || endDate ? { startDate, endDate } : undefined;

  useEffect(() => {
    let isMounted = true;

    const load = async (showSpinner: boolean) => {
      if (showSpinner) setLoading(true);
      try {
        const data = await fetchRecentScans(hospitalId, period, dateRange);
        if (!isMounted) return;
        setScans(limit ? data.slice(0, limit) : data);
        setError(null);
        setLastUpdated(new Date().toISOString());
      } catch (err) {
        if (!isMounted) return;
        console.error("Failed to fetch kiosk scans", err);
        setError("Unable to load kiosk scans right now.");
      } finally {
        if (isMounted && showSpinner) {
          setLoading(false);
        }
      }
    };

    void load(true);
    const interval = window.setInterval(() => {
      void load(false);
    }, pollMs);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [pollMs, limit, hospitalId, period, startDate, endDate]);

  return {
    scans,
    loading,
    error,
    lastUpdated,
    refresh: () => {
      void (async () => {
        try {
          const data = await fetchRecentScans(hospitalId, period, dateRange);
          setScans(limit ? data.slice(0, limit) : data);
          setLastUpdated(new Date().toISOString());
          setError(null);
        } catch (err) {
          console.error("Failed to fetch kiosk scans", err);
          setError("Unable to load kiosk scans right now.");
        }
      })();
    }
  };
}
